import { useTranslation } from 'react-i18next'
import { FieldError } from '@/components/ui/field-error'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import type { AudienceType, NewCampaign } from '@/services/crmCampaignsService'

export type CampaignBasics = Pick<
  NewCampaign,
  'nombre' | 'audiencia_tipo' | 'excluir_agentes_excluidos' | 'parar_si_responde'
>

interface CampaignBasicsFieldsProps {
  value: CampaignBasics
  nameError?: string
  onChange: (value: CampaignBasics) => void
}

/** Los campos de una campaña. Son los mismos al crearla y al editarla: un solo formulario. */
export function CampaignBasicsFields({ value, nameError, onChange }: CampaignBasicsFieldsProps) {
  const { t } = useTranslation()

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="campaign-name">{t('campaigns.editor.name_label')}</Label>
          <Input
            id="campaign-name"
            aria-label={t('campaigns.editor.name_label')}
            value={value.nombre}
            maxLength={200}
            placeholder={t('campaigns.editor.name_placeholder')}
            aria-invalid={Boolean(nameError)}
            aria-describedby={nameError ? 'campaign-name-error' : undefined}
            onChange={(event) => onChange({ ...value, nombre: event.target.value })}
          />
          <FieldError id="campaign-name-error">{nameError}</FieldError>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="campaign-audience-type">{t('campaigns.editor.audience_type_label')}</Label>
          <Select
            value={value.audiencia_tipo}
            onValueChange={(next) => onChange({ ...value, audiencia_tipo: next as AudienceType })}
          >
            <SelectTrigger id="campaign-audience-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="filtros">{t('campaigns.editor.audience_filters')}</SelectItem>
              <SelectItem value="csv">{t('campaigns.editor.audience_csv')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <Label htmlFor="campaign-skip-excluded">{t('campaigns.editor.skip_excluded_label')}</Label>
          <p className="text-xs text-muted-foreground">{t('campaigns.editor.skip_excluded_help')}</p>
        </div>
        <Switch
          id="campaign-skip-excluded"
          aria-label={t('campaigns.editor.skip_excluded_label')}
          checked={value.excluir_agentes_excluidos}
          onCheckedChange={(checked) => onChange({ ...value, excluir_agentes_excluidos: checked })}
        />
      </div>
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <Label htmlFor="campaign-stop-on-reply">{t('campaigns.editor.stop_on_reply_label')}</Label>
          <p className="text-xs text-muted-foreground">{t('campaigns.editor.stop_on_reply_help')}</p>
        </div>
        <Switch
          id="campaign-stop-on-reply"
          aria-label={t('campaigns.editor.stop_on_reply_label')}
          checked={value.parar_si_responde}
          onCheckedChange={(checked) => onChange({ ...value, parar_si_responde: checked })}
        />
      </div>
    </div>
  )
}
